
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Mail, Phone, School, Award, UserSquare } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface TeacherResultCardProps {
  teacher: any;
  query: string;
  highlightText: (text: string, highlight: string) => React.ReactNode;
}

const TeacherResultCard = ({ teacher, query, highlightText }: TeacherResultCardProps) => {
  const teacherName = `${teacher.firstName || ''} ${teacher.lastName || ''}`.trim();
  const initials = (teacher.firstName?.[0] || '') + (teacher.lastName?.[0] || '');
  
  return (
    <Link to={`/teachers/${teacher.id}`}>
      <Card className="mb-4 hover:shadow-md transition-shadow duration-200 overflow-hidden relative">
        <CardContent className="p-4 flex flex-col sm:flex-row gap-4 items-start">
          <Avatar className="h-12 w-12 border">
            <AvatarImage src={teacher.profileImage} alt={teacherName} />
            <AvatarFallback className="bg-blue-100 dark:bg-blue-800 text-blue-600 dark:text-blue-200">
              {initials}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-lg truncate">
              {highlightText(teacherName, query)}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-1 gap-x-4 mt-2">
              <div className="flex items-center text-sm text-muted-foreground">
                <UserSquare className="h-3.5 w-3.5 mr-2 flex-shrink-0" />
                <span className="truncate">{highlightText(teacher.id || 'N/A', query)}</span>
              </div>
              
              {teacher.email && (
                <div className="flex items-center text-sm text-muted-foreground">
                  <Mail className="h-3.5 w-3.5 mr-2 flex-shrink-0" />
                  <span className="truncate">{highlightText(teacher.email, query)}</span>
                </div>
              )}
              
              {teacher.phone && (
                <div className="flex items-center text-sm text-muted-foreground">
                  <Phone className="h-3.5 w-3.5 mr-2 flex-shrink-0" />
                  <span className="truncate">{highlightText(teacher.phone, query)}</span>
                </div>
              )}
              
              {teacher.department && (
                <div className="flex items-center text-sm text-muted-foreground">
                  <School className="h-3.5 w-3.5 mr-2 flex-shrink-0" />
                  <span className="truncate">{highlightText(teacher.department, query)}</span>
                </div>
              )}
              
              {teacher.qualifications && teacher.qualifications.length > 0 && (
                <div className="flex items-start sm:items-center text-sm text-muted-foreground">
                  <Award className="h-3.5 w-3.5 mr-2 mt-0.5 sm:mt-0 flex-shrink-0" />
                  <span className="truncate">
                    {teacher.qualifications.slice(0, 2).map((qual: any, idx: number) => (
                      <Badge key={idx} variant="outline" className="mr-1 mb-1">
                        {typeof qual === 'string' ? highlightText(qual, query) : highlightText(qual?.degree || '', query)}
                      </Badge>
                    ))}
                    {teacher.qualifications.length > 2 && (
                      <Badge variant="outline">+{teacher.qualifications.length - 2} more</Badge>
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
          
          <Badge variant="outline" className="absolute top-2 right-2 bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
            Teacher
          </Badge>
        </CardContent>
      </Card>
    </Link>
  );
};

export default TeacherResultCard;
